import { GroupToRights } from '@backoffice-frontend/common'
import { Claims } from '@moia-dev/moia-token-claims'
import type {
  AuthSession,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  fetchAuthSession as amplifyFetchAuthSession,
  fetchMFAPreference as amplifyFetchMFAPreference,
  getCurrentUser as amplifyGetCurrentUser,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updatePassword as amplifyUpdatePassword,
} from 'aws-amplify/auth'
import type { Hub as amplifyHub } from 'aws-amplify/utils'

type AuthListener = (data: { payload: { event: string } }) => void
const authListeners: Array<AuthListener> = []

/**
 * Set this to a temporary Backoffice session id to have the mocked token claim
 * the actor is inside that session, the way a real session token does. Unset mocks an actor in no session, which is what almost every real
 * user is.
 *
 * Set it from the devtools console, or in an E2E test via `setTemporarySessionId`
 * from `@backoffice-frontend/e2e-test`. The id the mock server's session store
 * hands out for this is `MOCK_ACTOR_TEMPORARY_SESSION_ID` in
 * `@backoffice-frontend/temporary-backoffice-sessions-fixtures`.
 */
const TEMPORARY_SESSION_ID_KEY = 'moia.local.amplifyAuthMock.temporarySessionId'

const getUserWithGroup = (emailOrUsername: string) => {
  const regex = /(\+\w*_*)/
  const result = emailOrUsername.match(regex)
  const groupKey = result?.at(0)?.replace('+', '')

  if (!groupKey) return undefined

  const normalizedGroupKey = Object.keys(GroupToRights).find(
    key => key.toLowerCase() === groupKey.toLowerCase(),
  )

  if (!normalizedGroupKey) return undefined

  return {
    groups: [normalizedGroupKey],
    rights: GroupToRights[normalizedGroupKey],
    username: `mock-user+${normalizedGroupKey}`,
    userId: `mock-user+${normalizedGroupKey}-id`,
  }
}

const getTokenFromMockUser = async (): Promise<string> => {
  const loggedInUser = await getCurrentUser()
  if (!loggedInUser) {
    throw new Error('No current user')
  }
  const { username } = loggedInUser

  const userWithGroup = getUserWithGroup(username)
  const fallbackUser = {
    groups: [],
    rights: [],
    username: 'mock-user+new',
    userId: `mock-user-${username}-id`,
  }

  const user = userWithGroup ?? fallbackUser

  const claims = Claims.fromSimpleClaims({
    defaultBOUM: {
      version: 'V2',
      backofficeUsername: user.username,
      userId: user.userId,
      isSystemUser: false,
      userRights: user.rights,
      fleets: [
        'f89ee35c-10ff-4d3b-959e-07d2e7637119', // hamburg fleet
        '3321dbea-f00b-4998-a3e1-a6c4b5898387', // hanover fleet
        '4bbfe046-7f37-44fe-bea8-6a2bc53b6241', // hamburg CUGT fleet
      ],
      ridepoolingServiceTenants: ['moia'],
      customerTenants: ['moia', 'switchh', 'mobli'],
      operatorTenants: ['moia'],
      serviceAreas: [
        'f89ee35c-10ff-4d3b-959e-07d2e7637119', // hamburg service area UUID
        '3321dbea-f00b-4998-a3e1-a6c4b5898387', // hanover service area UUID
        '6b17caac-df17-4130-8bce-4d62ff5ad518', // miami service area UUID
      ],
    },
    defaultCognito: {
      email: `${user.username}@example.com`,
    },
    defaultEmployee: {},
    defaultOpenID: {},
  }).toJSON()

  const temporarySessionId = localStorage.getItem(TEMPORARY_SESSION_ID_KEY)

  const header = { alg: 'RS256', fakeHeader: true }
  const encodedClaims = btoa(
    JSON.stringify({
      ...claims,
      // Not part of `Claims`, but the real token carries it while an actor is
      // inside a temporary session.
      ...(temporarySessionId ? { temporarySessionId } : {}),
    }),
  )
  const encodedHeader = btoa(JSON.stringify(header))
  const encodedSignature = btoa('fakeSignature')
  const fakeJwt = `${encodedHeader}.${encodedClaims}.${encodedSignature}`

  return fakeJwt
}

let signInInProgressUsername: string | null = null

export const signIn: typeof amplifySignIn = async ({ username }) => {
  signInInProgressUsername = username
  return {
    nextStep: { signInStep: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' },
    isSignedIn: false,
  }
}

export const confirmSignIn: typeof amplifyConfirmSignIn = async ({
  challengeResponse,
}) => {
  if (!signInInProgressUsername) {
    throw new Error('No sign in in progress')
  }
  if (challengeResponse === 'invalid-transfer-token') {
    signInInProgressUsername = null
    return Promise.reject({
      name: 'NotAuthorizedException',
      message: 'Invalid session for the user, session is expired.',
    })
  }
  localStorage.setItem(
    'moia.local.amplifyAuthMock.authenticatedUsername',
    signInInProgressUsername,
  )
  authListeners.forEach(listener => {
    listener({ payload: { event: 'signedIn' } })
  })
  return {
    nextStep: { signInStep: 'DONE' },
    isSignedIn: true,
  }
}

export const resetPassword: typeof amplifyResetPassword = async () => {
  return {
    isPasswordReset: true,
    nextStep: {
      resetPasswordStep: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
      codeDeliveryDetails: {},
    },
  }
}

export const getCurrentUser: typeof amplifyGetCurrentUser = async () => {
  const username = localStorage.getItem(
    'moia.local.amplifyAuthMock.authenticatedUsername',
  )

  if (!username) {
    throw new Error('No current user')
  }

  return { username, userId: `mock-user-${username}-id` }
}

export const fetchAuthSession: typeof amplifyFetchAuthSession = async () => {
  const token = await getTokenFromMockUser()
  return { tokens: { idToken: { toString: () => token } } } as AuthSession
}

export const fetchMFAPreference: typeof amplifyFetchMFAPreference =
  async () => {
    return { preferred: 'TOTP' }
  }

export const Hub: typeof amplifyHub = {
  listen: (_channel: string, callback: AuthListener) => {
    authListeners.push(callback)
    return () => {
      const index = authListeners.indexOf(callback)
      if (index !== -1) {
        authListeners.splice(index, 1)
      }
    }
  },
} as typeof amplifyHub

export const signOut: typeof amplifySignOut = async () => {
  localStorage.removeItem('moia.local.amplifyAuthMock.authenticatedUsername')
  localStorage.removeItem(TEMPORARY_SESSION_ID_KEY)
  authListeners.forEach(listener => {
    listener({ payload: { event: 'signedOut' } })
  })
}

const COGNITO_PASS_REGEXP =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`])\S{6,99}$/

export const updatePassword: typeof amplifyUpdatePassword = async ({
  newPassword,
}) => {
  if (!COGNITO_PASS_REGEXP.test(newPassword)) {
    return Promise.reject({
      name: 'InvalidPasswordException',
      message: 'Password did not conform with policy: Password not long enough',
    })
  }
}

export const confirmResetPassword: typeof amplifyConfirmResetPassword = async ({
  newPassword,
}) => {
  if (newPassword === 'CodeMismatchException') {
    return Promise.reject({
      name: 'CodeMismatchException',
      message: 'Invalid verification code provided, please try again.',
    })
  }
  if (newPassword === 'LimitExceededException') {
    return Promise.reject({
      name: 'LimitExceededException',
      message:
        'Attempt limit exceeded, please try after some time. (Service: AWSCognitoIdentityProviderService; Status Code: 400; Error Code: LimitExceededException; Request ID: ...)',
    })
  }
  if (newPassword === 'InvalidPasswordException') {
    return Promise.reject({
      name: 'InvalidPasswordException',
      message: 'Password did not conform with policy: Password not long enough',
    })
  }
  if (newPassword === 'fails') {
    return Promise.reject({
      name: 'SomeOtherException',
      message: 'Some other error message',
    })
  }
}
